import { AUTH_TYPE_KEY, AUTH_TYPES } from '@brightdrop/auth-client';
import { getFormAsState, useForm, validEmailRegex } from '@brightdrop/bd-form';
import { BrandLogo, Button, HeroImage } from '@brightdrop/bd-ui';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import {
  REGIONAL_DEFAULTS,
  type SupportedLocale,
} from '@brightdrop/localization-core';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import {
  createElement,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '~/app/store';
import ErrorIcon from '~/assets/icons/new/error.svg?react';
import LocaleSwitcher from '~/common/components/LocaleSwitcher/LocaleSwitcher';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import { useGmFleetSupportEmail } from '~/common/hooks/useOnStarFleetIntelligenceSupportEmail';
import useSearchParams from '~/common/hooks/useSearchParams';
import {
  GM_FLEET_REBRAND_FLAG,
  ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG,
} from '~/common/models/featureFlags.model';
import { AppRoutePaths } from '~/common/models/route.model';
import { validateSsoEmail } from '~/features/users/usersSlice';
import { InactivityStorageKeys } from '~/hooks/useInactivityTracker/InactivityTracker.constants';
import useStyles from './EmailLoginForm.styles';

enum EmailLoginField {
  EMAIL = 'email',
}

export type EmailLoginFormFields = {
  email: string | null;
};

export type ValidEmailLoginForm = Pick<EmailLoginFormFields, 'email'>;

export interface EmailLoginFormProps {
  onSubmit: (email?: string) => void;
  onCreateAccount?: () => void;
  displayCdiInterface: boolean;
  authenticationType?: string;
  /**
   * A redirect has been requested and the browser has not left the page yet.
   * The form stays mounted through that window, so the controls have to hold
   * it: a second `loginRedirect` while one is pending fails outright.
   */
  isSubmitting?: boolean;
  /** Locale sent to B2C with the sign-in redirect. */
  locale?: SupportedLocale;
  onLocaleChange?: (locale: SupportedLocale) => void;
}

const MAX_EMAIL_LENGTH = 254;

const emailValidator = (value: EmailLoginFormFields[EmailLoginField.EMAIL]) => {
  return !!value && validEmailRegex.test(value);
};

const EmailLoginForm = ({
  onSubmit,
  onCreateAccount,
  displayCdiInterface = true,
  authenticationType = AUTH_TYPES.CDI,
  isSubmitting = false,
  locale = REGIONAL_DEFAULTS.locale,
  onLocaleChange,
}: EmailLoginFormProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { email, autosubmit } = useSearchParams();
  const { hash, search } = useLocation();
  const { getFlag } = useFeatureFlags();
  const isGmFleetRebrandEnabled = !!getFlag(GM_FLEET_REBRAND_FLAG);
  const isOnStarIntelligenceRebrandEnabled = !!getFlag(
    ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG
  );

  const supportEmail = useGmFleetSupportEmail();
  const { classes } = useStyles();

  const [helpMessageLink, setHelpMessageLink] = useState<ReactElement | null>(
    null
  );
  const [canAutoSubmit, setCanAutoSubmit] = useState<boolean>(
    autosubmit?.toLowerCase() === 'true'
  );
  const initialFormState = useMemo(
    () =>
      getFormAsState({
        email: '',
      } as EmailLoginFormFields),
    []
  );
  const [isValidationReady, setIsValidationReady] = useState<boolean>(false);
  const [isValidSsoEmail, setIsValidSsoEmail] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const validateSsoCallback = useCallback(
    async (email: string) => {
      setIsValidationReady(false);
      setIsValidating(true);
      try {
        const validateSsoResponse = await dispatch(
          validateSsoEmail({ email: email })
        );
        if (validateSsoResponse.meta.requestStatus) {
          setIsValidSsoEmail(
            validateSsoResponse.meta.requestStatus === 'fulfilled'
          );
          return validateSsoResponse.meta.requestStatus === 'fulfilled';
        }
      } catch (error) {
        console.error('Validate sso failed due to: ', error);
        setIsValidSsoEmail(false);
      } finally {
        setIsValidationReady(true);
        setIsValidating(false);
      }
    },
    [dispatch]
  );

  //Due to validator logic occurring in the same render cycle as change formState
  //validator is used in external components of form instead of internal form component
  const validateSsoEmailInput = useCallback(
    async (email: string) => {
      //The input does not need to be validated if the user logs in via CDI
      if (authenticationType === AUTH_TYPES.CDI) {
        setIsValidationReady(false);
        setIsValidSsoEmail(true);
        setIsValidationReady(true);
        return true;
      }
      if (authenticationType === AUTH_TYPES.SSO && emailValidator(email)) {
        return await validateSsoCallback(email);
      }
      setIsValidationReady(true);
    },
    [authenticationType, validateSsoCallback]
  );

  // useForm test validation results in an error when trying to type in email with more than max length
  // will have to revisit and update this later
  const validationSchema = useMemo(
    () => ({
      email: {
        required: true,
        maxLength: MAX_EMAIL_LENGTH,
        validators: [
          {
            validator: emailValidator,
            error: translateText('common:errors.emailRequired'),
          },
        ],
      },
    }),
    []
  );

  const { isDirty, formState, handleChange, formStateIsValid, updateErrors } =
    useForm<EmailLoginFormFields, ValidEmailLoginForm>(
      initialFormState,
      validationSchema
    );
  const handleSubmit = useCallback(
    async (event: React.SyntheticEvent | CustomEvent) => {
      event.preventDefault();

      if (isSubmitting) {
        return;
      }

      //Validation should only happen on submit
      const isEmailValidated = await validateSsoEmailInput(
        formState.email.value || ''
      );
      if (!isEmailValidated) {
        setCanAutoSubmit(false);
      }

      const shouldValidateForm = authenticationType === AUTH_TYPES.SSO;
      const isFormValid = shouldValidateForm
        ? formStateIsValid(formState)
        : true;

      if (isFormValid && isEmailValidated && !isValidating) {
        sessionStorage.removeItem(InactivityStorageKeys.userSignedOut);
        onSubmit(formState.email.value || '');
      } else {
        // ensure errors are up-to-date for any dependent components
        updateErrors();
        //Set the Error message here so that it remains constant until the user hits submit again
        if (isFormValid) {
          setErrorMessage(translateText('common:invalid_sso_email'));
        } else {
          setErrorMessage(translateText('common:valid_email_required'));
        }
      }
    },
    [
      validateSsoEmailInput,
      formState,
      formStateIsValid,
      isSubmitting,
      isValidating,
      onSubmit,
      updateErrors,
      authenticationType,
    ]
  );

  const isSsoInterface = authenticationType === AUTH_TYPES.SSO;
  const isBusy = isValidating || isSubmitting;

  /**
   * Switches between the SSO and CDI login forms. Existing query params and the
   * hash carry over so a deep link survives the switch, and `authType` is
   * overwritten rather than appended so bouncing between the two forms cannot
   * stack up duplicate values. A full page load is required because the MSAL
   * instance is built from `authType` when the app boots, so the form and the
   * authority it posts to would otherwise disagree.
   */
  const handleClick = () => {
    const url = new URL(AppRoutePaths.LOGIN, window.location.origin);
    const searchParams = new URLSearchParams(search);
    if (isSsoInterface) {
      searchParams.delete(AUTH_TYPE_KEY);
    } else {
      searchParams.set(AUTH_TYPE_KEY, AUTH_TYPES.SSO.valueOf());
    }
    url.search = searchParams.toString();
    url.hash = hash;
    window.location.href = url.toString();
  };

  const renderAlternateAuthLink = () => {
    const alternateLinkTranslationKey = isSsoInterface
      ? 'common:sign_in_as_gm_customer'
      : 'common:sign_in_as_gm_employee';

    // View Height units used to make the padding and margin responsive to the window height, going with 1024px as the height for desktop
    return (
      <Box className={classes.useSsoDiv} sx={{ mt: '3.90625vh' }}>
        <Button
          onClick={handleClick}
          data-testid="email-login-form-alt-auth-login-link"
          variant="button-link"
        >
          <TranslatedLabel
            translationKey={alternateLinkTranslationKey}
            typographyVariant="body_medium"
          />
        </Button>
      </Box>
    );
  };

  const errorHelperTextComp = () => {
    return (
      <>
        <ErrorIcon
          data-testid={'email-login-form-error-icon'}
          className={classes.formErrorSvg}
        />{' '}
        {errorMessage}
      </>
    );
  };

  const loginText = displayCdiInterface
    ? translateText(
        isOnStarIntelligenceRebrandEnabled
          ? 'common:welcome_to_onstar_intelligence_platform'
          : isGmFleetRebrandEnabled
            ? 'common:welcome_to_onstar_platform'
            : 'common:welcome_to_app'
      )
    : translateText('common:sign_in_with_sso');

  const loginHeader =
    sessionStorage.getItem(InactivityStorageKeys.userSignedOut) === 'true'
      ? translateText('common:inactivity:loginHeader')
      : loginText;

  const loginAgainMessage = () => {
    return sessionStorage.getItem(InactivityStorageKeys.userSignedOut) ===
      'true' ? (
      <Typography className={classes.loginText}>
        {translateText('common:inactivity:loginAgainMessage')}
      </Typography>
    ) : (
      <></>
    );
  };

  const handleCreateAccount = useCallback(() => {
    if (onCreateAccount) {
      sessionStorage.removeItem(InactivityStorageKeys.userSignedOut);
      onCreateAccount();
    }
  }, [onCreateAccount]);

  const renderCdiButtons = () => {
    return (
      <Box width="350px">
        <Box
          width={1}
          className={classes.formSubmitRow}
          gap={2}
          display="flex"
          flexDirection="column"
        >
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            data-testid="cdi-sign-in-button"
          >
            {translateText('common:sign_in')}
          </Button>
          <Box display="flex" alignItems="center" width="100%" my={1}>
            <Box flex={1} height="1px" bgcolor="divider" />
            <Typography variant="body_medium" color="text.secondary" mx={2}>
              {translateText('common:or')}
            </Typography>
            <Box flex={1} height="1px" bgcolor="divider" />
          </Box>
          <Button
            variant="secondary"
            onClick={handleCreateAccount}
            disabled={isSubmitting}
            data-testid="cdi-create-account-button"
          >
            {translateText('common:create_account')}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderSsoForm = () => {
    return (
      <Box
        sx={{
          width: '90dvw',
          maxWidth: '350px',
        }}
      >
        <Box width={1}>
          <TextField
            data-heap-redact-text
            label={translateText('common:email')}
            variant="filled"
            size="small"
            onChange={(e) => {
              //whitespace is not allowed on the ends of the input
              //Note: does not prevent whitespace between characters in the input
              handleChange(EmailLoginField.EMAIL, e.target.value.trim());
            }}
            type="text"
            name={EmailLoginField.EMAIL}
            id={EmailLoginField.EMAIL}
            placeholder={translateText('common:email')}
            value={formState.email.value}
            inputProps={{ maxLength: validationSchema.email.maxLength }}
            error={
              (!isValidSsoEmail || !formStateIsValid(formState)) &&
              isDirty &&
              errorMessage !== '' &&
              isValidationReady
            }
            helperText={
              isValidationReady &&
              (!isValidSsoEmail || !formStateIsValid(formState)) &&
              isDirty &&
              errorMessage !== '' &&
              errorHelperTextComp()
            }
            disabled={isBusy}
          />
        </Box>
        <Box width={1} className={classes.formSubmitRow}>
          <Button
            type="submit"
            //Button will be enabled by default, until validation has begun
            disabled={isBusy}
            className={isBusy ? classes.continueButtonValidating : ''}
          >
            {isBusy ? (
              <CircularProgress size={25} className={classes.loaderIcon} />
            ) : (
              translateText('common:continue')
            )}
          </Button>
        </Box>
      </Box>
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: seeds the form from the email query param once on mount; re-running on email/handleChange changes would override user edits
  useEffect(() => {
    if (email) {
      //whitespace is not allowed on the ends of the input
      //Note: does not prevent whitespace between characters in the input
      handleChange(EmailLoginField.EMAIL, email.trim());
    }
  }, []);

  //Trigger auto-submit here when initial conditions are met
  //(i.e. valid email and auto-submit query parameter present)
  useEffect(() => {
    if (isDirty && !formStateIsValid(formState)) {
      setCanAutoSubmit(false);
    }
    if (canAutoSubmit && formStateIsValid(formState)) {
      handleSubmit(
        new CustomEvent('submit', { bubbles: true, cancelable: true })
      ).then();
    }
  }, [canAutoSubmit, formState, formStateIsValid, handleSubmit, isDirty]);

  // Build the support mailto link client-side so it is not present in static HTML.
  useEffect(() => {
    const supportEmailText = (
      <TranslatedLabel
        typographyVariant="body_medium"
        translationKey="common:support.contactSupport"
      />
    );
    const supportEmailElem = createElement(
      'a',
      {
        href: `mailto:${supportEmail}`,
        'data-testid': 'email-login-form-support-link',
      },
      supportEmailText
    );
    setHelpMessageLink(supportEmailElem);
  }, [supportEmail]);

  return (
    <Box className={classes.emailLoginWrapper}>
      {onLocaleChange && (
        <LocaleSwitcher
          variant="compact"
          className={classes.localeSwitcher}
          value={locale}
          onChange={onLocaleChange}
          testId="login-locale-select"
          size="small"
          disabled={isBusy}
        />
      )}
      <HeroImage className={classes.emailLoginLeftCol} />
      <div className={classes.emailLoginRightCol}>
        <div className={classes.emailLoginFormContent}>
          <div className={classes.companyLogo}>
            <BrandLogo />
          </div>
          <form
            onSubmit={handleSubmit}
            id="email-login-form"
            noValidate
            className={classes.formContainer}
          >
            <Box py="40px">
              <Typography
                variant="header1_medium"
                className={classes.loginText}
                //Note: span needed here to not break functional tests for login
                component="span"
              >
                {loginHeader}
              </Typography>
              {isOnStarIntelligenceRebrandEnabled && displayCdiInterface && (
                <Typography
                  variant="body_regular"
                  color="text.secondary"
                  sx={{ mt: '8px' }}
                >
                  {translateText(
                    'common:welcome_to_onstar_intelligence_platform_message'
                  )}
                </Typography>
              )}
            </Box>
            {loginAgainMessage()}
            {displayCdiInterface ? renderCdiButtons() : renderSsoForm()}
            <div>
              {renderAlternateAuthLink()}
              <Box className={classes.helpTextDiv}>
                <span>
                  <TranslatedLabel
                    typographyVariant="body_medium"
                    translationKey="common:need_help_message"
                  />
                </span>
                {helpMessageLink}
              </Box>
            </div>
          </form>
        </div>
      </div>
    </Box>
  );
};

export default EmailLoginForm;
